import React, { useState, useMemo, useEffect } from 'react';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import ActivityTimeline from './components/ActivityTimeline';
import ActivityFilters from './components/ActivityFilters';
import QuickAddActivity from './components/QuickAddActivity';
import BulkActions from './components/BulkActions';
import ActivityStats from './components/ActivityStats';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import SampleDataToggle from '../../components/ui/SampleDataToggle';

import { sampleDataService } from '../../services/sampleDataService';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';


export default function Activities() {
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [activityToEdit, setActivityToEdit] = useState(null);
  const [activityToReschedule, setActivityToReschedule] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleActivities, setSampleActivities] = useState([]);

  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
    owner: 'all',
    priority: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Read initial state from localStorage
  const getInitialSampleDataState = () => {
    try {
      const stored = localStorage?.getItem(`crm_sample_data_enabled_${profile?.tenant_id}`);
      return stored === 'true';
    } catch {
      return false;
    }
  };

  // Real-time activities synchronization
  const transformActivity = (dbActivity) => ({
    id: dbActivity?.id,
    tenantId: dbActivity?.tenant_id,
    type: dbActivity?.type,
    title: dbActivity?.title,
    description: dbActivity?.description,
    dueDate: dbActivity?.due_date,
    status: dbActivity?.status,
    priority: dbActivity?.priority,
    assignedTo: dbActivity?.assigned_to,
    relatedTo: dbActivity?.related_to,
    relatedType: dbActivity?.related_type,
    completedAt: dbActivity?.completed_at,
    createdAt: dbActivity?.created_at,
    updatedAt: dbActivity?.updated_at
  });

  const { data: realtimeActivities, setData: setRealtimeActivities, isConnected } = useRealtimeEntity(
    'activities',
    activities,
    transformActivity
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setActivities(realtimeActivities);
    }
  }, [realtimeActivities, isConnected]);

  useEffect(() => {
    loadActivities();
  }, [profile?.tenant_id]);

  // Load sample data if toggle is already active on mount
  useEffect(() => {
    if (showSampleData && profile?.tenant_id) {
      loadSampleData();
    }
  }, [profile?.tenant_id]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      // Load activities logic here
      // This function was being called but never declared
      setLoading(false);
    } catch (error) {
      console.error('Error loading activities:', error);
      setLoading(false);
    }
  };

  const loadSampleData = async () => {
    try {
      const data = await sampleDataService?.getSampleDataByEntity('activities', profile?.tenant_id);
      setSampleActivities(data || []);
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        await loadSampleData();
      } else {
        setSampleActivities([]);
      }
    } catch (err) {
      console.error('Error toggling sample data:', err);
    }
  };

  const filteredActivities = useMemo(() => {
    // Combine real and sample data if sample data is active
    const allActivities = showSampleData ? [...activities, ...sampleActivities] : activities;
    
    return allActivities?.filter(activity => {
      // Search filter
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        const matchesSearch = 
          activity?.title?.toLowerCase()?.includes(searchTerm) ||
          activity?.description?.toLowerCase()?.includes(searchTerm) ||
          (activity?.contact && activity?.contact?.toLowerCase()?.includes(searchTerm)) ||
          (activity?.account && activity?.account?.toLowerCase()?.includes(searchTerm));
        
        if (!matchesSearch) return false;
      }

      // Type filter
      if (filters?.type !== 'all' && activity?.type !== filters?.type) {
        return false;
      }

      // Status filter
      if (filters?.status !== 'all') {
        const now = new Date();
        const isOverdue = !activity?.completed && new Date(activity.dueDate) < now;
        
        if (filters?.status === 'completed' && !activity?.completed) return false;
        if (filters?.status === 'pending' && (activity?.completed || isOverdue)) return false;
        if (filters?.status === 'overdue' && !isOverdue) return false;
      }

      // Owner filter
      if (filters?.owner !== 'all') {
        const ownerMap = {
          'john-doe': 'John Doe',
          'sarah-wilson': 'Sarah Wilson',
          'mike-johnson': 'Mike Johnson',
          'emily-davis': 'Emily Davis',
          'alex-brown': 'Alex Brown'
        };
        
        if (activity?.owner !== ownerMap?.[filters?.owner]) return false;
      }

      // Priority filter
      if (filters?.priority !== 'all' && activity?.priority !== filters?.priority) {
        return false;
      }

      // Date range filter
      if (filters?.dateFrom) {
        const activityDate = new Date(activity.dueDate);
        const fromDate = new Date(filters.dateFrom);
        if (activityDate < fromDate) return false;
      }

      if (filters?.dateTo) {
        const activityDate = new Date(activity.dueDate);
        const toDate = new Date(filters.dateTo);
        toDate?.setHours(23, 59, 59, 999); // End of day
        if (activityDate > toDate) return false;
      }

      return true;
    })?.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [activities, filters, showSampleData, sampleActivities]);

  const handleMenuToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      status: 'all',
      owner: 'all',
      priority: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleAddActivity = (newActivity) => {
    setActivities(prev => [newActivity, ...prev]);
  };

  const handleEditActivity = (activity) => {
    setActivityToEdit(activity);
    setIsEditModalOpen(true);
  };

  const handleSaveEditedActivity = (updatedData) => {
    setActivities(prev => 
      prev?.map(activity => 
        activity?.id === activityToEdit?.id
          ? { ...activity, ...updatedData }
          : activity
      )
    );
    setIsEditModalOpen(false);
    setActivityToEdit(null);
  };

  const handleCompleteActivity = (activityId) => {
    setActivities(prev => 
      prev?.map(activity => 
        activity?.id === activityId 
          ? { ...activity, completed: true }
          : activity
      )
    );
  };

  const handleRescheduleActivity = (activity) => {
    setActivityToReschedule(activity);
    setIsRescheduleModalOpen(true);
  };

  const handleSaveRescheduledActivity = (newDate) => {
    setActivities(prev => 
      prev?.map(activity => 
        activity?.id === activityToReschedule?.id
          ? { ...activity, dueDate: newDate }
          : activity
      )
    );
    setIsRescheduleModalOpen(false);
    setActivityToReschedule(null);
  };

  const handleSelectActivity = (activityId, checked) => {
    if (checked) {
      setSelectedActivities(prev => [...prev, activityId]);
    } else {
      setSelectedActivities(prev => prev?.filter(id => id !== activityId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedActivities(filteredActivities?.map(a => a?.id));
    } else {
      setSelectedActivities([]);
    }
  };

  const handleBulkComplete = () => {
    setActivities(prev => 
      prev?.map(activity => 
        selectedActivities?.includes(activity?.id)
          ? { ...activity, completed: true }
          : activity
      )
    );
    setSelectedActivities([]);
  };

  const handleBulkReassign = (newOwner) => {
    setActivities(prev => 
      prev?.map(activity => 
        selectedActivities?.includes(activity?.id)
          ? { ...activity, owner: newOwner }
          : activity
      )
    );
    setSelectedActivities([]);
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedActivities?.length} activities?`)) {
      setActivities(prev => 
        prev?.filter(activity => !selectedActivities?.includes(activity?.id))
      );
      setSelectedActivities([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedActivities([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <ComponentIdBadge
        id="pg-activities-main"
        name="Activities Main Page"
        description="Activity management with timeline, filters, and bulk actions"
      />
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6 relative">
          <div>
            <ComponentIdBadge
              id="hdr-activities-title"
              name="Activities Page Header"
              description="Page title and activity statistics"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Activities</h1>
            <p className="text-gray-600">
              Track and manage your sales activities, tasks, and follow-ups
            </p>
          </div>

          <div className="flex items-center gap-3">
            <SampleDataToggle
              entityType="activities"
              onToggle={handleSampleDataToggle}
              isActive={showSampleData}
            />
            <Button
              variant="default"
              onClick={() => setIsQuickAddOpen(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Quick Add Activity
            </Button>
          </div>
        </div>

        {/* Activity Stats */}
        <ActivityStats activities={activities} />

        {/* Activity Filters */}
        <ActivityFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          totalCount={activities?.length}
          filteredCount={filteredActivities?.length}
        />

        {/* Bulk Actions */}
        <BulkActions
          selectedCount={selectedActivities?.length}
          onMarkComplete={handleBulkComplete}
          onReassign={handleBulkReassign}
          onDelete={handleBulkDelete}
          onClearSelection={handleClearSelection}
        />

        {/* Activities List */}
        <div className="bg-card border border-border rounded-lg">
          {/* List Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-4">
              <Checkbox
                checked={selectedActivities?.length === filteredActivities?.length && filteredActivities?.length > 0}
                onChange={(e) => handleSelectAll(e?.target?.checked)}
                className="mr-2"
              />
              <h3 className="text-lg font-semibold text-foreground">
                Activity Timeline ({filteredActivities?.length})
              </h3>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Export
              </Button>
              <Button variant="outline" size="sm" iconName="RefreshCw">
                Refresh
              </Button>
            </div>
          </div>

          {/* Activities Timeline */}
          <div className="p-6">
            {filteredActivities?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No activities found</h3>
                <p className="text-muted-foreground mb-4">
                  {activities?.length === 0 
                    ? "Get started by creating your first activity" :"Try adjusting your filters to see more activities"
                  }
                </p>
                <Button
                  variant="outline"
                  onClick={() => setIsQuickAddOpen(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Activity
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Selection checkboxes for timeline items */}
                {filteredActivities?.map((activity) => (
                  <div key={activity?.id} className="flex items-start space-x-4">
                    <Checkbox
                      checked={selectedActivities?.includes(activity?.id)}
                      onChange={(e) => handleSelectActivity(activity?.id, e?.target?.checked)}
                      className="mt-6"
                    />
                    <div className="flex-1">
                      <ActivityTimeline
                        activities={[activity]}
                        onEdit={handleEditActivity}
                        onComplete={handleCompleteActivity}
                        onReschedule={handleRescheduleActivity}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Quick Add Activity Modal */}
        <QuickAddActivity
          isOpen={isQuickAddOpen}
          onClose={() => setIsQuickAddOpen(false)}
          onAdd={handleAddActivity}
          initialData={null}
        />

        {/* Edit Activity Modal */}
        {isEditModalOpen && (
          <QuickAddActivity
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setActivityToEdit(null);
            }}
            onAdd={handleSaveEditedActivity}
            initialData={activityToEdit}
          />
        )}

        {/* Reschedule Modal */}
        {isRescheduleModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Reschedule Activity</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Date & Time
                </label>
                <input
                  type="datetime-local"
                  defaultValue={activityToReschedule?.dueDate?.toISOString()?.slice(0, 16)}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    handleSaveRescheduledActivity(newDate);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRescheduleModalOpen(false);
                    setActivityToReschedule(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}